"use client";

import { Capacitor } from "@capacitor/core";
import { useSerwist } from "@serwist/turbopack/react";
import { useEffect } from "react";
import { createClientLogger } from "@/lib/logging/client";

const logger = createClientLogger("components/service-worker-registration");

/**
 * Kill switch, so the service worker can be ruled out without a code change.
 *
 * A page controlled by a service worker routes EVERY request it makes through
 * that worker's fetch handler — including requests to other origins, and
 * including ones the browser starts itself rather than the page's JavaScript.
 * That makes it the one component in the browser that can stall unrelated
 * things simultaneously.
 *
 * Which is the shape production keeps showing. In one capture the route, the
 * control probe and the prefetch probe all aborted at exactly their 8s
 * deadline, `apple-touch-icon.png` hung beside them, and analytics posts to
 * `buzz.hiive.club` — a different hostname on a different IP, so a different
 * HTTP/2 connection — took 15.4s at the same moment. No single connection
 * explains that, and with `NEXT_PUBLIC_WS_URL` cleared the realtime socket
 * cannot either. `apple-touch-icon.png` is the discriminating one: the browser
 * fetches it on its own, so a busy main thread does not explain it, and a
 * service worker does.
 *
 * Set NEXT_PUBLIC_HIIVE_SERVICE_WORKER=false to turn it off. Existing
 * installations are actively unregistered rather than merely skipped — a
 * client already controlled by a worker stays controlled otherwise, which
 * would make the test a no-op for exactly the sessions that have the problem.
 *
 * Cache Storage is deliberately left alone. Unregistering is enough to take
 * the worker out of the request path, while clearing `/_next/static/` would
 * hand back the ChunkLoadError that cache exists to prevent.
 *
 * Offline support goes with it, so this is a diagnostic setting, not a
 * destination.
 */
const serviceWorkerEnabled =
  process.env.NEXT_PUBLIC_HIIVE_SERVICE_WORKER !== "false";

/** Register after mount and retry a transient network failure on reconnect. */
export function ServiceWorkerRegistration() {
  const { serwist } = useSerwist();
  useEffect(() => {
    if (!serviceWorkerEnabled) {
      void (async () => {
        if (!("serviceWorker" in navigator)) return;
        try {
          const registrations =
            await navigator.serviceWorker.getRegistrations();
          if (registrations.length === 0) return;
          await Promise.all(
            registrations.map((registration) => registration.unregister()),
          );
          // The current document stays controlled until it is replaced; the
          // next load is the one that runs without a worker.
          logger.warn("service_worker_disabled_and_unregistered", {
            count: registrations.length,
          });
        } catch (error) {
          logger.warn("service_worker_unregister_failed", {
            message: error instanceof Error ? error.message : String(error),
          });
        }
      })();
      return;
    }
    if (!serwist || Capacitor.isNativePlatform()) return;
    let inFlight = false;
    let registered = false;
    const register = async () => {
      if (inFlight || registered) return;
      inFlight = true;
      try {
        await serwist.register();
        registered = true;
      } catch (error) {
        logger.warn("service_worker_registration_failed", {
          message: error instanceof Error ? error.message : String(error),
        });
      } finally {
        inFlight = false;
      }
    };
    void register();
    window.addEventListener("online", register);
    return () => window.removeEventListener("online", register);
  }, [serwist]);
  return null;
}
